/**
 * Client-side audit overview insights (mirrors @seo/renderer audit-insights).
 * Kept local so the web app does not pull Puppeteer via @seo/renderer.
 */
import type { Audit, AuditIssue, AuditPageSummary } from '@seo/types';

export interface OverviewInsights {
  priorityFixes: Array<{
    priority: number;
    issue: string;
    impact: string;
    effort: string;
    affectedPages: number;
  }>;
  trafficImpact: {
    potentialGain: string;
    gainNote: string;
    levers: string[];
  };
  categoryScores: Array<{ label: string; score: number }>;
  aiSummary: string;
  beforeAfter: Array<{ label: string; current: string; target: string }>;
  pagePriority: Array<{ label: string; starDisplay: string; hint: string }>;
}

const CHECK_LABELS: Record<string, string> = {
  broken_links: 'Broken Links',
  canonical_missing: 'Missing Canonicals',
  canonical_conflict: 'Canonical Conflicts',
  missing_meta_description: 'Missing Meta Descriptions',
  thin_content: 'Thin Content',
  dup_titles: 'Duplicate Titles',
  missing_alt: 'Missing Image Alt Text',
  core_web_vitals: 'Core Web Vitals',
  schema_org_validity: 'Schema / Structured Data',
  orphan_pages: 'Orphan Pages',
  https_not_enforced: 'HTTPS Not Enforced',
  hsts_missing: 'HSTS Missing',
  mixed_content: 'Mixed Content',
  noindex_on_indexable_template: 'Noindex on Live Pages',
  title_length: 'Title Length Issues',
  meta_length: 'Meta Description Length',
};

export function buildOverviewInsights(
  audit: Audit,
  issues: AuditIssue[],
  pages: AuditPageSummary[],
): OverviewInsights {
  const pagesCrawled = Math.max(1, audit.pages_crawled ?? pages.length ?? 1);
  const ranked = [...issues].sort(
    (a, b) =>
      priorityScore(b, pagesCrawled) - priorityScore(a, pagesCrawled) ||
      b.affected_url_count - a.affected_url_count,
  );

  const priorityFixes = ranked.slice(0, 10).map((issue, i) => ({
    priority: i + 1,
    issue: CHECK_LABELS[issue.check_id] ?? issue.title,
    impact: impactLabel(issueImpact(issue, pagesCrawled)),
    effort: effortLabel(issueEase(issue)),
    affectedPages: issue.affected_url_count,
  }));

  const highSeverity = ranked.filter(
    (i) => i.severity === 'critical' || i.severity === 'high',
  ).length;
  const health = audit.health_score ?? 0;

  let potentialGain = '+2–5%';
  let gainNote = 'Site is already healthy — gains come from polish and content depth';
  if (highSeverity >= 8 || health < 50) {
    potentialGain = '+20–35%';
    gainNote = 'Estimated after fixing Critical and High severity issues';
  } else if (highSeverity >= 3 || health < 70) {
    potentialGain = '+10–20%';
    gainNote = 'Estimated after fixing High severity issues';
  } else if (highSeverity >= 1 || health < 85) {
    potentialGain = '+5–12%';
    gainNote = 'Estimated after fixing the top priority issues';
  }

  const ids = new Set(issues.map((i) => i.check_id));
  const levers: string[] = [];
  if (ids.has('broken_links') || ids.has('orphan_pages')) levers.push('Crawl efficiency');
  if (ids.has('missing_meta_description') || ids.has('dup_titles')) levers.push('CTR');
  if (ids.has('canonical_missing') || ids.has('noindex_on_indexable_template')) {
    levers.push('Indexability');
  }
  if (ids.has('schema_org_validity')) levers.push('Rich snippets');
  levers.push('Organic traffic');

  const sub = audit.sub_scores;
  const categoryScores = [
    { label: 'Technical', score: sub?.technical ?? 0 },
    { label: 'Content', score: sub?.content ?? 0 },
    {
      label: 'UX',
      score: Math.round(((sub?.on_page ?? 0) + (sub?.indexing ?? 0)) / 2),
    },
    { label: 'Performance', score: sub?.performance ?? 0 },
    {
      label: 'Accessibility',
      score: scoreFromChecks(issues, pagesCrawled, ['missing_alt', 'heading_hierarchy_skipped']),
    },
    {
      label: 'Schema',
      score: scoreFromChecks(issues, pagesCrawled, ['schema_org_validity']),
    },
    {
      label: 'Security',
      score: scoreFromChecks(issues, pagesCrawled, [
        'https_not_enforced',
        'hsts_missing',
        'mixed_content',
      ]),
    },
    {
      label: 'Internal Links',
      score: scoreFromChecks(issues, pagesCrawled, ['orphan_pages', 'broken_links']),
    },
  ];

  const top = priorityFixes.slice(0, 3).map((f) => f.issue.toLowerCase());
  const fixPhrase =
    top.length === 0
      ? 'the highest-impact issues below'
      : top.length === 1
        ? top[0]
        : `${top.slice(0, -1).join(', ')}, and ${top[top.length - 1]}`;
  let posture = 'uneven — several template-level problems are capping growth';
  if (health >= 90) posture = 'technically strong overall';
  else if (health >= 70) posture = 'solid in places but held back by a few fundamental gaps';
  else if (health < 50) posture = 'underperforming on core SEO basics';

  const aiSummary =
    `The website is ${posture}, but missing fundamental SEO optimizations such as ${fixPhrase}. ` +
    `Fixing these alone would likely produce the largest improvement.`;

  const beforeAfter = ranked.slice(0, 6).map((i) => ({
    label: CHECK_LABELS[i.check_id] ?? i.title,
    current: `${i.affected_url_count} page${i.affected_url_count === 1 ? '' : 's'}`,
    target: '0 pages',
  }));

  const pagePriority = buildPagePriority(pages);

  return {
    priorityFixes,
    trafficImpact: { potentialGain, gainNote, levers: [...new Set(levers)] },
    categoryScores,
    aiSummary,
    beforeAfter,
    pagePriority,
  };
}

function priorityScore(issue: AuditIssue, pagesCrawled: number): number {
  return issueImpact(issue, pagesCrawled) * 0.7 + issueEase(issue) * 0.3;
}

function issueImpact(issue: AuditIssue, pagesCrawled: number): number {
  if (issue.impact_score != null) return issue.impact_score;
  const sev: Record<string, number> = { critical: 90, high: 70, medium: 45, low: 25 };
  const base = sev[issue.severity] ?? 40;
  const coverage = Math.min(1, issue.affected_url_count / pagesCrawled);
  return Math.min(100, base + Math.round(coverage * 20));
}

function issueEase(issue: AuditIssue): number {
  if (issue.ease_score != null) return issue.ease_score;
  const n = issue.affected_url_count;
  if (n <= 1) return 80;
  if (n <= 10) return 60;
  if (n <= 100) return 45;
  return 30;
}

function impactLabel(score: number): string {
  if (score >= 80) return 'Very High';
  if (score >= 60) return 'High';
  if (score >= 40) return 'Medium';
  return 'Low';
}

function effortLabel(score: number): string {
  if (score >= 65) return 'Low';
  if (score >= 40) return 'Medium';
  return 'High';
}

function scoreFromChecks(
  issues: AuditIssue[],
  pagesCrawled: number,
  checkIds: string[],
): number {
  const relevant = issues.filter((i) => checkIds.includes(i.check_id));
  if (relevant.length === 0) return 100;
  let deduction = 0;
  for (const i of relevant) {
    const coverage = Math.min(1, i.affected_url_count / pagesCrawled);
    const sev: Record<string, number> = { critical: 35, high: 22, medium: 12, low: 5 };
    deduction += (sev[i.severity] ?? 10) * (0.35 + 0.65 * Math.sqrt(coverage));
  }
  return Math.max(0, Math.min(100, Math.round(100 - deduction)));
}

function buildPagePriority(
  pages: AuditPageSummary[],
): Array<{ label: string; starDisplay: string; hint: string }> {
  let hasHome = false;
  let hasBlog = false;
  let hasProduct = false;
  let hasLegal = false;
  for (const p of pages) {
    const path = (() => {
      try {
        return new URL(p.url).pathname.toLowerCase();
      } catch {
        return p.url.toLowerCase();
      }
    })();
    if (path === '/' || path === '') hasHome = true;
    if (/\/(blog|news|articles?)(\/|$)/.test(path)) hasBlog = true;
    if (/\/(product|pricing|shop|services?)(\/|$)/.test(path)) hasProduct = true;
    if (/\/(privacy|terms|legal)(\/|$)/.test(path)) hasLegal = true;
  }
  const stars = (n: number) => '★'.repeat(n) + '☆'.repeat(5 - n);
  const rows: Array<{ label: string; starDisplay: string; hint: string }> = [];
  if (hasHome) rows.push({ label: 'Homepage', starDisplay: stars(5), hint: 'Highest business value — fix first' });
  if (hasBlog) rows.push({ label: 'Blog', starDisplay: stars(5), hint: 'Drives long-term organic discovery' });
  if (hasProduct) rows.push({ label: 'Product Pages', starDisplay: stars(4), hint: 'Directly tied to conversions' });
  if (hasLegal) rows.push({ label: 'Legal Pages', starDisplay: stars(2), hint: 'Needed for trust, lower SEO priority' });
  if (rows.length === 0) {
    rows.push(
      { label: 'Homepage', starDisplay: stars(5), hint: 'Highest business value — fix first' },
      { label: 'Key landing pages', starDisplay: stars(4), hint: 'Prioritize pages that convert' },
    );
  }
  return rows;
}
